import './database-table.web.css';

import { createElement, useCallback, useEffect, useRef, useState } from 'react';

function selectInputText(event: Event) {
  const input = event.target as HTMLInputElement | null;
  input?.select();
}
import { ActivityIndicator, Pressable, Text, View } from 'react-native';

import { FavoriteStarButton } from '@/components/layout/FavoriteStarButton.web';
import { PrivateFlagButton } from '@/components/layout/PrivateFlagButton.web';
import { fetchBlock, updateBlock } from '@/lib/blocks/api';
import { newBlockId } from '@/lib/blocks/ids';
import type { BlockRecord } from '@/lib/blocks/types';
import {
  createDatabaseRow,
  downloadDatabaseExport,
  fetchDatabaseSchema,
  importDatabase,
  updateDatabaseSchema,
  upsertRowProperty,
} from '@/lib/databases/api';
import { parseCsvToImportTable } from '@/lib/databases/import-csv';
import type {
  BlockPropertyRecord,
  DatabaseColumnDefinition,
  DatabaseFooterResult,
  DatabaseQueryGroup,
  DatabaseSchemaRecord,
} from '@/lib/databases/types';
import { formatGroupKey } from '@/lib/databases/build-query';
import { queryDatabaseForView } from '@/lib/databases/query-all';
import { colorColumns } from '@/lib/databases/row-color';
import { applyDatabaseSearch } from '@/lib/databases/filter-query-results';
import {
  getActiveView,
  queryGroupByForView,
  querySortsForView,
  TITLE_COLUMN_WIDTH_KEY,
  updateView,
  type DatabaseView,
  type ImportMappingInput,
  type ImportTable,
} from '@couplespace/core/databases';
import {
  autoSizeAllColumnWidths,
  autoSizeDataColumnWidth,
  type ColumnAutoSizeInput,
} from '@/lib/databases/column-auto-size.web';
import {
  clampColumnWidth,
  columnWidthPx,
  sumTableColumnWidths,
  toggleHiddenColumn,
  visibleColumns,
} from '@/lib/databases/column-layout';
import type { ColumnResizeHandlers } from '@/lib/databases/column-resize.web';
import {
  createBlockViewStore,
  readViewConfigFromBlock,
  type DatabaseViewConfig,
  type ViewStore,
} from '@/lib/databases/view-config';
import { boardDropPropertyValue } from '@/lib/databases/board-lanes';
import {
  canChangeColumnType,
  ensureOption,
  type AggregateFunction,
  type DateFormat,
  type SelectOption,
} from '@/lib/databases/values';
import { stopBubbleUnlessFormField } from '@/lib/databases/stop-editor-bubble';
import {
  boardSetupRequirement,
  boardViewNeedsColumnPick,
  boardViewNeedsSetup,
  calendarSetupRequirement,
  calendarViewNeedsColumnPick,
  calendarViewNeedsSetup,
  dateColumns,
  selectColumns,
  timelineSetupRequirement,
  timelineViewNeedsColumnPick,
  timelineViewNeedsSetup,
} from '@/lib/databases/view-requirements';

import {
  ColumnHeader,
  ColumnMenusHost,
  TitleColumnHeader,
  type ColumnMenuState,
} from './ColumnMenu.web';
import { DatabaseRowPanel } from './DatabaseRowPanel.web';
import { DatabaseBoard } from './DatabaseBoard.web';
import { DatabaseCalendar } from './DatabaseCalendar.web';
import { DatabaseTimeline } from './DatabaseTimeline.web';
import { DatabaseList } from './DatabaseList.web';
import { DatabaseViewSetup } from './DatabaseViewSetup.web';
import { DatabaseCell } from './DatabaseCell.web';
import { hasFooterAggregates } from './CalculateMenuPanel.web';
import { DatabaseFooterRow } from './DatabaseFooterRow.web';
import { ImportDialog } from './ImportDialog.web';
import { DatabaseToolbar } from './DatabaseToolbar.web';
import { DatabaseViewTabs } from './DatabaseViewTabs.web';
import { useSidebar } from '@/components/layout/SidebarContext';
import { subscribeLiveDeltas, subscribeLiveReconnect } from '@/lib/live/connection';
import { routeTouchesDatabase } from '@/lib/live/route-delta';

const LIVE_REFETCH_DEBOUNCE_MS = 300;

type DatabaseTableProps = {
  databaseId: string;
  viewStore?: ViewStore;
  onTitleChange?: (title: string) => void;
  onOpenDatabase?: () => void;
  onOpenRow?: (rowId: string) => void;
};

type EditingCell = { rowId: string; propertyId: string };

function isConflictError(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return false;
  }
  return (
    err.message.includes('CONFLICT') || err.message.includes('modified by another session')
  );
}

export function DatabaseTable({
  databaseId,
  viewStore: viewStoreProp,
  onTitleChange,
  onOpenDatabase,
  onOpenRow,
}: DatabaseTableProps) {
  const { updateChild } = useSidebar();
  const [block, setBlock] = useState<BlockRecord | null>(null);
  const [schema, setSchema] = useState<DatabaseSchemaRecord | null>(null);
  const [rows, setRows] = useState<BlockRecord[]>([]);
  const [propertiesByRowId, setPropertiesByRowId] = useState<
    Record<string, BlockPropertyRecord[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newRowDraft, setNewRowDraft] = useState('');
  const [creatingRow, setCreatingRow] = useState(false);
  const [schemaSaving, setSchemaSaving] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDbTitle, setEditingDbTitle] = useState(false);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editingTitleRowId, setEditingTitleRowId] = useState<string | null>(null);
  const [titleDraftByRowId, setTitleDraftByRowId] = useState<Record<string, string>>({});
  const [columnMenu, setColumnMenu] = useState<ColumnMenuState>({ kind: 'closed' });
  const columnHeaderRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [viewConfig, setViewConfig] = useState<DatabaseViewConfig | null>(() =>
    viewStoreProp ? viewStoreProp.read() : null,
  );
  const [groups, setGroups] = useState<DatabaseQueryGroup[]>([]);
  const [queryFooters, setQueryFooters] = useState<Record<string, DatabaseFooterResult>>({});
  const [queryLoading, setQueryLoading] = useState(false);
  const queryRequestRef = useRef(0);
  const [peekRowId, setPeekRowId] = useState<string | null>(null);
  const csvInputRef = useRef<HTMLInputElement | null>(null);
  const [importTable, setImportTable] = useState<ImportTable | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const viewSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [liveColumnWidths, setLiveColumnWidths] = useState<Record<string, number> | null>(null);
  const [isColumnResizing, setIsColumnResizing] = useState(false);
  const viewConfigRef = useRef<DatabaseViewConfig | null>(null);
  const blockViewStoreRef = useRef<ViewStore | null>(null);
  if (!viewStoreProp && !blockViewStoreRef.current) {
    blockViewStoreRef.current = createBlockViewStore(databaseId, {
      getCachedBlock: () => block,
      onBlockUpdated: (updated) => setBlock(updated),
    });
  }
  const viewStore = viewStoreProp ?? blockViewStoreRef.current!;

  const activeView = viewConfig ? getActiveView(viewConfig) : null;
  const showFooterRow = hasFooterAggregates(activeView?.columnAggregates);
  const filters = activeView?.filters ?? [];
  const sorts = activeView?.sorts ?? [];
  const isBoardView = activeView?.type === 'board';
  const isListView = activeView?.type === 'list';
  const isCalendarView = activeView?.type === 'calendar';
  const isTimelineView = activeView?.type === 'timeline';
  const isTableView = !isBoardView && !isListView && !isCalendarView && !isTimelineView;

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchDebounced(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    viewConfigRef.current = viewConfig;
  }, [viewConfig]);

  useEffect(() => {
    setLiveColumnWidths(null);
  }, [activeView?.id]);

  useEffect(() => {
    return () => {
      if (viewSaveTimerRef.current) {
        clearTimeout(viewSaveTimerRef.current);
      }
    };
  }, []);

  const persistViewConfig = useCallback(
    async (next: DatabaseViewConfig, options?: { immediate?: boolean }) => {
      const run = async () => {
        try {
          await viewStore.save(next);
          setViewConfig(viewStore.read());
        } catch (err) {
          if (isConflictError(err) && !viewStoreProp) {
            try {
              const row = await fetchBlock(databaseId);
              setBlock(row);
              setViewConfig(readViewConfigFromBlock(row));
            } catch {
              setError(err instanceof Error ? err.message : 'Failed to save view');
            }
          } else {
            setError(err instanceof Error ? err.message : 'Failed to save view');
          }
        }
      };
      if (options?.immediate) {
        if (viewSaveTimerRef.current) {
          clearTimeout(viewSaveTimerRef.current);
          viewSaveTimerRef.current = null;
        }
        await run();
        return;
      }
      if (viewSaveTimerRef.current) {
        clearTimeout(viewSaveTimerRef.current);
      }
      viewSaveTimerRef.current = setTimeout(() => {
        viewSaveTimerRef.current = null;
        void run();
      }, 500);
    },
    [databaseId, viewStore, viewStoreProp],
  );

  function handleViewConfigChange(
    next: DatabaseViewConfig,
    options?: { immediate?: boolean },
  ) {
    setViewConfig(next);
    void persistViewConfig(next, options);
  }

  async function addColumnForViewSetup(spec: {
    name: string;
    type: string;
    config?: Record<string, unknown>;
  }): Promise<string | undefined> {
    if (!schema || schemaSaving) {
      return undefined;
    }
    const id = newBlockId();
    setSchemaSaving(true);
    setError(null);
    try {
      const updated = await updateDatabaseSchema(databaseId, {
        columns: [...schema.columns, { id, name: spec.name, type: spec.type, config: spec.config }],
        expectedSyncVersion: schema.syncVersion,
      });
      setSchema(updated);
      return id;
    } catch (err) {
      try {
        await handleConflict(err);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to add column');
      }
      return undefined;
    } finally {
      setSchemaSaving(false);
    }
  }

  async function handleAddBoardStatusColumn() {
    const spec = boardSetupRequirement().column;
    const colId = await addColumnForViewSetup(spec);
    if (colId && viewConfig) {
      patchActiveView({ boardColumnId: colId }, { immediate: true });
    }
  }

  async function handleAddCalendarDateColumn() {
    const spec = calendarSetupRequirement().column;
    const colId = await addColumnForViewSetup(spec);
    if (colId && viewConfig) {
      patchActiveView({ calendarColumnId: colId }, { immediate: true });
    }
  }

  async function handleAddTimelineDateColumn() {
    const spec = timelineSetupRequirement().column;
    const colId = await addColumnForViewSetup(spec);
    if (colId && viewConfig) {
      patchActiveView({ timelineColumnId: colId }, { immediate: true });
    }
  }

  function patchActiveView(
    patch: Partial<Omit<DatabaseView, 'id'>>,
    options?: { immediate?: boolean },
  ) {
    if (!viewConfig) {
      return;
    }
    const active = getActiveView(viewConfig);
    handleViewConfigChange(updateView(viewConfig, active.id, patch), options);
  }

  function handleColumnHeaderClick(columnId: string) {
    setColumnMenu((prev) => {
      if (prev.kind === 'menu' && prev.columnId === columnId && prev.panel === 'main') {
        return { kind: 'closed' };
      }
      return { kind: 'menu', columnId, panel: 'main' };
    });
  }

  const runQuery = useCallback(async (options?: { silent?: boolean }) => {
    if (!schema || !activeView) {
      return;
    }
    const queryGroupBy = queryGroupByForView(activeView);
    const useGrouped = queryGroupBy !== null;
    const requestId = ++queryRequestRef.current;
    if (!options?.silent) {
      setQueryLoading(true);
      setError(null);
    }
    try {
      const raw = await queryDatabaseForView(databaseId, activeView, searchDebounced, {
        columnAggregates: activeView.columnAggregates,
      });
      if (requestId !== queryRequestRef.current) {
        return;
      }
      const result = applyDatabaseSearch(raw, searchDebounced);
      setQueryFooters(result.footers ?? {});
      if (useGrouped) {
        const nextGroups = result.groups ?? [];
        setGroups(nextGroups);
        setRows([]);
        const merged: Record<string, BlockPropertyRecord[]> = {};
        for (const group of nextGroups) {
          if (group.propertiesByRowId) {
            Object.assign(merged, group.propertiesByRowId);
          }
        }
        setPropertiesByRowId(merged);
      } else {
        setGroups([]);
        setRows(result.rows);
        setPropertiesByRowId(result.propertiesByRowId ?? {});
      }
    } catch (err) {
      if (!options?.silent) {
        setError(err instanceof Error ? err.message : 'Failed to load rows');
      }
    } finally {
      if (!options?.silent) {
        setQueryLoading(false);
      }
    }
  }, [databaseId, schema, searchDebounced, viewConfig]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const row = await fetchBlock(databaseId);
      if (row.type !== 'database') {
        setError('This block is not a database');
        return;
      }
      setBlock(row);
      setTitleDraft(row.title ?? '');
      if (!viewStoreProp) {
        setViewConfig(readViewConfigFromBlock(row));
      } else {
        setViewConfig(viewStore.read());
      }
      const schemaRecord = await fetchDatabaseSchema(databaseId);
      setSchema(schemaRecord);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Database not found');
    } finally {
      setLoading(false);
    }
  }, [databaseId]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const row = await fetchBlock(databaseId);
      if (row.type !== 'database') {
        setError('This block is not a database');
        return;
      }
      setBlock(row);
      setTitleDraft(row.title ?? '');
      if (!viewStoreProp) {
        setViewConfig(readViewConfigFromBlock(row));
      } else {
        setViewConfig(viewStore.read());
      }
      const schemaRecord = await fetchDatabaseSchema(databaseId);
      setSchema(schemaRecord);
      const config = viewStoreProp ? viewStore.read() : readViewConfigFromBlock(row);
      const active = getActiveView(config);
      const queryGroupBy = queryGroupByForView(active);
      const useGrouped = queryGroupBy !== null;
      setQueryLoading(true);
      const raw = await queryDatabaseForView(databaseId, active, searchDebounced);
      const result = applyDatabaseSearch(raw, searchDebounced);
      if (useGrouped) {
        const nextGroups = result.groups ?? [];
        setGroups(nextGroups);
        setRows([]);
        const merged: Record<string, BlockPropertyRecord[]> = {};
        for (const group of nextGroups) {
          if (group.propertiesByRowId) {
            Object.assign(merged, group.propertiesByRowId);
          }
        }
        setPropertiesByRowId(merged);
      } else {
        setGroups([]);
        setRows(result.rows);
        setPropertiesByRowId(result.propertiesByRowId ?? {});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Database not found');
    } finally {
      setLoading(false);
      setQueryLoading(false);
    }
  }, [databaseId, searchDebounced, viewConfig]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (schema && viewConfig) {
      void runQuery();
    }
  }, [schema, viewConfig, runQuery]);

  const runQueryRef = useRef(runQuery);
  runQueryRef.current = runQuery;
  const schemaRef = useRef(schema);
  schemaRef.current = schema;
  const loadedRowIdsRef = useRef<Set<string>>(new Set());
  loadedRowIdsRef.current = new Set([
    ...rows.map((row) => row.id),
    ...groups.flatMap((group) => group.rows.map((row) => row.id)),
  ]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let refetchSchema = false;
    const schedule = (withSchema: boolean) => {
      refetchSchema = refetchSchema || withSchema;
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        timer = null;
        const needsSchema = refetchSchema;
        refetchSchema = false;
        if (needsSchema) {
          void fetchDatabaseSchema(databaseId)
            .then(setSchema)
            .catch(() => {});
          return;
        }
        void runQueryRef.current({ silent: true });
      }, LIVE_REFETCH_DEBOUNCE_MS);
    };
    const unsubscribeDeltas = subscribeLiveDeltas((route) => {
      const currentSchema = schemaRef.current;
      if (
        !routeTouchesDatabase(route, {
          databaseBlockId: databaseId,
          schemaDatabaseId: currentSchema?.databaseId ?? null,
          rowIds: loadedRowIdsRef.current,
        })
      ) {
        return;
      }
      const schemaChanged =
        route.refetchAll ||
        (currentSchema !== null && route.schemaDatabaseIds.has(currentSchema.databaseId));
      schedule(schemaChanged);
    });
    const unsubscribeReconnect = subscribeLiveReconnect(() => schedule(true));
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      unsubscribeDeltas();
      unsubscribeReconnect();
    };
  }, [databaseId]);

  async function handleConflict(err: unknown): Promise<void> {
    if (isConflictError(err)) {
      setError('Someone else changed this database. Reloading…');
      await reload();
      return;
    }
    throw err;
  }

  function openCsvFilePicker() {
    csvInputRef.current?.click();
  }

  async function handleCsvFileSelected(file: File) {
    const text = await file.text();
    const table = parseCsvToImportTable(text, { name: file.name.replace(/\.csv$/i, '') });
    if (table.rows.length === 0) {
      setError('CSV has no data rows');
      return;
    }
    setImportTable(table);
    setImportDialogOpen(true);
  }

  async function handleImportConfirm(mapping: ImportMappingInput) {
    if (!importTable) {
      return;
    }
    setSchemaSaving(true);
    setError(null);
    try {
      await importDatabase(databaseId, { table: importTable, mapping });
      setImportDialogOpen(false);
      setImportTable(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      throw err;
    } finally {
      setSchemaSaving(false);
    }
  }

  async function handleExport(format: 'csv' | 'json' | 'markdown') {
    if (!activeView) {
      return;
    }
    setExportBusy(true);
    setError(null);
    try {
      await downloadDatabaseExport(databaseId, {
        format,
        search: searchDebounced || undefined,
        filter: activeView.filters,
        sort: querySortsForView(activeView),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExportBusy(false);
    }
  }

  async function persistSchema(nextColumns: DatabaseColumnDefinition[]) {
    if (!schema || schemaSaving) {
      return;
    }
    setSchemaSaving(true);
    setError(null);
    try {
      const updated = await updateDatabaseSchema(databaseId, {
        columns: nextColumns,
        expectedSyncVersion: schema.syncVersion,
      });
      setSchema(updated);
    } catch (err) {
      try {
        await handleConflict(err);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update columns');
      }
    } finally {
      setSchemaSaving(false);
    }
  }

  async function ensureSelectOption(columnId: string, name: string): Promise<SelectOption> {
    const col = columns.find((c) => c.id === columnId);
    if (!col) {
      throw new Error('Column not found');
    }
    const { config, option, created } = ensureOption(col.config, name);
    if (created) {
      await persistSchema(
        columns.map((c) => (c.id === columnId ? { ...c, config } : c)),
      );
    }
    return option;
  }

  async function patchColumnDateFormat(columnId: string, format: DateFormat) {
    await persistSchema(
      columns.map((c) =>
        c.id === columnId
          ? { ...c, config: { ...(c.config ?? {}), dateFormat: format } }
          : c,
      ),
    );
  }

  async function patchColumnConfig(
    columnId: string,
    config: Record<string, unknown>,
  ) {
    await persistSchema(
      columns.map((c) => (c.id === columnId ? { ...c, config } : c)),
    );
  }

  async function saveCell(
    rowId: string,
    propertyId: string,
    columnType: string,
    value: Record<string, unknown> | null,
  ) {
    const payload =
      value ??
      (columnType === 'checkbox'
        ? { t: 'checkbox', v: false }
        : columnType === 'number'
          ? { t: 'number', v: null }
          : { t: columnType, v: '' });

    try {
      const existing = (propertiesByRowId[rowId] ?? []).find((p) => p.propertyId === propertyId);
      const saved = await upsertRowProperty(rowId, propertyId, payload, {
        expectedSyncVersion: existing?.syncVersion,
      });
      setPropertiesByRowId((prev) => {
        const list = [...(prev[rowId] ?? [])];
        const idx = list.findIndex((p) => p.propertyId === propertyId);
        if (idx >= 0) {
          list[idx] = saved;
        } else {
          list.push(saved);
        }
        return { ...prev, [rowId]: list };
      });
    } catch (err) {
      try {
        await handleConflict(err);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save cell');
      }
    }
  }

  async function commitNewRow() {
    const title = newRowDraft.trim();
    if (!title || creatingRow || schemaSaving) {
      return;
    }
    setCreatingRow(true);
    setError(null);
    try {
      await createDatabaseRow(databaseId, { title });
      setNewRowDraft('');
      await runQuery();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add row');
    } finally {
      setCreatingRow(false);
    }
  }

  function findRowById(rowId: string): BlockRecord | undefined {
    const flat = rows.find((r) => r.id === rowId);
    if (flat) {
      return flat;
    }
    for (const group of groups) {
      const inGroup = group.rows.find((r) => r.id === rowId);
      if (inGroup) {
        return inGroup;
      }
    }
    return undefined;
  }

  async function commitRowTitle(rowId: string) {
    const draft = titleDraftByRowId[rowId];
    const row = findRowById(rowId);
    if (draft === undefined || !row) {
      setEditingTitleRowId(null);
      return;
    }
    const next = draft.trim() || 'Untitled';
    if (next === (row.title ?? '')) {
      setEditingTitleRowId(null);
      return;
    }
    try {
      const updated = await updateBlock(rowId, {
        title: next,
        expectedSyncVersion: row.syncVersion,
      });
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, title: updated.title } : r)));
    } catch (err) {
      try {
        await handleConflict(err);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to rename row');
      }
    }
    setEditingTitleRowId(null);
  }

  function startEditingDbTitle() {
    setTitleDraft(block?.title ?? '');
    setEditingDbTitle(true);
  }

  async function commitTitle() {
    setEditingDbTitle(false);
    const next = titleDraft.trim() || 'Untitled database';
    if (!block || next === (block.title ?? '')) {
      return;
    }
    try {
      const updated = await updateBlock(databaseId, {
        title: next,
        expectedSyncVersion: block.syncVersion,
      });
      setBlock((prev) => (prev ? { ...prev, title: updated.title, syncVersion: updated.syncVersion } : prev));
      setTitleDraft(updated.title ?? next);
      onTitleChange?.(updated.title ?? next);
      if (updated.parentId) {
        updateChild(updated.parentId, updated.id, {
          title: updated.title,
          syncVersion: updated.syncVersion,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename database');
    }
  }

  const columns = schema?.columns ?? [];
  const visibleCols = isTableView
    ? visibleColumns(columns, activeView?.hiddenColumns)
    : columns;
  const frozenTitle = isTableView && activeView?.frozenTitle === true;
  const wrapText = isTableView && activeView?.wrapText === true;
  const bodyColSpan = visibleCols.length + 1;
  const tableGroupBy = activeView?.type === 'table' ? activeView.groupBy : null;

  function resolveColumnWidth(columnKey: string, fallback: number): number {
    const live = liveColumnWidths?.[columnKey];
    if (typeof live === 'number') {
      return live;
    }
    return columnWidthPx(activeView?.columnWidths, columnKey, fallback) ?? fallback;
  }

  function setLiveWidth(columnKey: string, width: number) {
    setLiveColumnWidths((prev) => ({
      ...(activeView?.columnWidths ?? {}),
      ...prev,
      [columnKey]: clampColumnWidth(width),
    }));
  }

  function commitWidths(patch: Record<string, number>) {
    setLiveColumnWidths(null);
    if (!viewConfig || !activeView) {
      return;
    }
    const merged = { ...(activeView.columnWidths ?? {}) };
    for (const [key, width] of Object.entries(patch)) {
      merged[key] = clampColumnWidth(width);
    }
    patchActiveView({ columnWidths: merged }, { immediate: true });
  }

  function rowsForAutoSize(): BlockRecord[] {
    if (tableGroupBy && groups.length > 0) {
      return groups.flatMap((g) => g.rows);
    }
    return rows;
  }

  function buildAutoSizeInput(): ColumnAutoSizeInput {
    return {
      columns,
      visibleCols,
      rows: rowsForAutoSize(),
      propertiesByRowId,
    };
  }

  const titleWidth = resolveColumnWidth(TITLE_COLUMN_WIDTH_KEY, 160);

  const titleResizeHandlers: ColumnResizeHandlers = {
    onLive: (w) => setLiveWidth(TITLE_COLUMN_WIDTH_KEY, w),
    onCommit: (w) => commitWidths({ [TITLE_COLUMN_WIDTH_KEY]: w }),
    onAutoSize: () => commitWidths(autoSizeAllColumnWidths(buildAutoSizeInput())),
  };

  function columnResizeHandlers(columnId: string): ColumnResizeHandlers {
    return {
      onLive: (w) => setLiveWidth(columnId, w),
      onCommit: (w) => commitWidths({ [columnId]: w }),
      onAutoSize: () =>
        commitWidths({
          [columnId]: autoSizeDataColumnWidth(columnId, buildAutoSizeInput()),
        }),
    };
  }

  const tableClassName = [
    wrapText ? 'cs-db-table cs-db-wrap' : 'cs-db-table',
    isColumnResizing ? 'cs-db-table-resizing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const tableWidthPx = sumTableColumnWidths(
    titleWidth,
    visibleCols.map((col) => col.id),
    (columnId) => resolveColumnWidth(columnId, 120),
  );
  const boardColumnId = activeView?.boardColumnId;
  const boardColumn = boardColumnId
    ? columns.find((c) => c.id === boardColumnId)
    : undefined;
  const boardSelectColumns = selectColumns(columns);
  const boardSetup = boardViewNeedsSetup(columns);
  const boardPickColumn = boardViewNeedsColumnPick(columns, boardColumnId);
  const calendarColumnId = activeView?.calendarColumnId;
  const calendarSetup = calendarViewNeedsSetup(columns);
  const calendarPickColumn = calendarViewNeedsColumnPick(columns, calendarColumnId);
  const timelineColumnId = activeView?.timelineColumnId;
  const timelineSetup = timelineViewNeedsSetup(columns);
  const timelinePickColumn = timelineViewNeedsColumnPick(columns, timelineColumnId);
  const dateCols = dateColumns(columns);
  const colorCols = colorColumns(columns);
  const colorColumnId = colorCols.some((column) => column.id === activeView?.colorColumnId)
    ? activeView?.colorColumnId
    : undefined;
  const colorColumn = colorCols.find((column) => column.id === colorColumnId);
  async function moveBoardCard(rowId: string, laneKey: string | null) {
    if (!boardColumnId || schemaSaving) {
      return;
    }
    await saveCell(rowId, boardColumnId, 'select', boardDropPropertyValue(laneKey));
    await runQuery();
  }

  function renderDataRow(row: BlockRecord) {
    const titleTdClass = frozenTitle
      ? 'cs-db-td-title cs-db-td-frozen'
      : 'cs-db-td-title';
    return createElement(
      'tr',
      { key: row.id, className: 'cs-db-tr' },
      createElement(
        'td',
        {
          className: titleTdClass,
          style: { width: titleWidth, minWidth: titleWidth, maxWidth: titleWidth },
        },
        editingTitleRowId === row.id
          ? createElement('input', {
              type: 'text',
              className: 'cs-db-title-input',
              value: titleDraftByRowId[row.id] ?? row.title ?? '',
              autoFocus: true,
              onChange: (e: Event) =>
                setTitleDraftByRowId((prev) => ({
                  ...prev,
                  [row.id]: (e.target as HTMLInputElement).value,
                })),
              onBlur: () => void commitRowTitle(row.id),
              onKeyDown: (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const el = e.currentTarget as HTMLInputElement;
                  void commitRowTitle(row.id).then(() => el.blur());
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setEditingTitleRowId(null);
                  (e.currentTarget as HTMLInputElement).blur();
                  return;
                }
                e.stopPropagation();
              },
              onMouseDown: (e: MouseEvent) => e.stopPropagation(),
              onClick: (e: MouseEvent) => e.stopPropagation(),
            })
          : createElement(
              'div',
              { className: 'cs-db-td-title-inner' },
              createElement(
                'span',
                {
                  className: 'cs-db-title-text',
                  onDoubleClick: (e: MouseEvent) => {
                    e.stopPropagation();
                    setEditingTitleRowId(row.id);
                    setTitleDraftByRowId((prev) => ({
                      ...prev,
                      [row.id]: row.title ?? '',
                    }));
                  },
                  onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                },
                row.title ?? 'Untitled',
              ),
              createElement(
                'button',
                {
                  type: 'button',
                  className: 'cs-db-peek-btn cs-db-td-title-open',
                  onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                  onClick: (e: MouseEvent) => {
                    e.stopPropagation();
                    setPeekRowId(row.id);
                  },
                },
                'Open',
              ),
            ),
      ),
      ...visibleCols.map((col) => {
        const colWidth = resolveColumnWidth(col.id, 120);
        const cell = (propertiesByRowId[row.id] ?? []).find((p) => p.propertyId === col.id);
        const editing =
          editingCell?.rowId === row.id && editingCell.propertyId === col.id;
        const tdAlignClass =
          col.type === 'checkbox' || col.type === 'date'
            ? 'cs-db-td-cell--center'
            : 'cs-db-td-cell--left';
        return createElement(
          'td',
          {
            key: col.id,
            className: `cs-db-td-cell ${tdAlignClass}`,
            style: { width: colWidth, minWidth: colWidth, maxWidth: colWidth },
          },
          createElement(DatabaseCell, {
            column: col,
            property: cell,
            disabled: schemaSaving,
            isEditing: editing,
            onStartEdit: () => setEditingCell({ rowId: row.id, propertyId: col.id }),
            onEndEdit: () => setEditingCell(null),
            onSave: (value) => saveCell(row.id, col.id, col.type, value),
            onEnsureSelectOption: ensureSelectOption,
            onDateFormatChange: patchColumnDateFormat,
          }),
        );
      }),
    );
  }

  function renderNewRowRow() {
    return createElement(
      'tr',
      { key: '__new-row', className: 'cs-db-tr' },
      createElement(
        'td',
        { className: 'cs-db-td-title' },
        createElement('input', {
          type: 'text',
          className: 'cs-db-new-row-input',
          value: newRowDraft,
          disabled: creatingRow || schemaSaving,
          placeholder: '',
          onChange: (e: Event) => setNewRowDraft((e.target as HTMLInputElement).value),
          onBlur: () => void commitNewRow(),
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const el = e.currentTarget as HTMLInputElement;
              void commitNewRow().then(() => el.blur());
              return;
            }
            e.stopPropagation();
          },
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          onClick: (e: MouseEvent) => e.stopPropagation(),
        }),
      ),
      ...visibleCols.map((col) =>
        createElement('td', { key: col.id, className: 'cs-db-td-cell' }),
      ),
    );
  }

  function handleFooterAggregateChange(propertyId: string, fn: AggregateFunction | null) {
    const prev = activeView?.columnAggregates ?? {};
    const next: Record<string, AggregateFunction> = { ...prev };
    if (fn) {
      next[propertyId] = fn;
    } else {
      delete next[propertyId];
    }
    const columnAggregates =
      Object.keys(next).length > 0 ? next : undefined;
    patchActiveView({ columnAggregates });
  }

  function renderFooterRow(footers?: Record<string, DatabaseFooterResult>) {
    return createElement(DatabaseFooterRow, {
      visibleCols,
      footers,
      columnAggregates: activeView?.columnAggregates,
      disabled: schemaSaving,
      titleWidth,
      dataColumnWidth: (colId) => resolveColumnWidth(colId, 120),
      onAggregateChange: handleFooterAggregateChange,
    });
  }

  function renderFlatBody() {
    return [
      createElement(
        'tbody',
        { key: 'db-body' },
        ...rows.map((row) => renderDataRow(row)),
        renderNewRowRow(),
      ),
      showFooterRow
        ? createElement('tfoot', { key: 'db-foot' }, renderFooterRow(queryFooters))
        : null,
    ];
  }

  function renderGroupedBodies() {
    const sections =
      groups.length === 0
        ? []
        : groups.map((group) =>
            createElement(
              'tbody',
              { key: group.key ?? '__null__', className: 'cs-db-group-tbody' },
              createElement(
                'tr',
                { className: 'cs-db-tr cs-db-group-header-row' },
                createElement(
                  'td',
                  { colSpan: bodyColSpan, className: 'cs-db-group-header' },
                  `${formatGroupKey(group.key, tableGroupBy!, columns)} (${group.rows.length})`,
                ),
              ),
              ...group.rows.map((row) => renderDataRow(row)),
              showFooterRow ? renderFooterRow(group.footers) : null,
            ),
          );
    return [
      ...sections,
      createElement('tbody', { key: '__new' }, renderNewRowRow()),
      showFooterRow
        ? createElement('tfoot', { key: 'db-foot' }, renderFooterRow(queryFooters))
        : null,
    ];
  }

  if (loading) {
    return (
      <View className="items-center justify-center py-8">
        <ActivityIndicator size="small" />
      </View>
    );
  }

  if (error || !block) {
    return (
      <View className="items-center justify-center py-6">
        <Text className="text-sm text-fg-muted">{error ?? 'Database not found'}</Text>
      </View>
    );
  }

  return (
    <View className="cs-db-root w-full min-w-0 max-w-full">
      <View className="cs-db-db-title-row flex-row flex-wrap items-center gap-2">
        {createElement(
          'div',
          { className: 'min-w-0 flex-1' },
          editingDbTitle
            ? createElement('input', {
                type: 'text',
                className: 'cs-db-title-input cs-db-db-title-edit',
                value: titleDraft,
                placeholder: 'Untitled database',
                autoFocus: true,
                onChange: (e: Event) =>
                  setTitleDraft((e.target as HTMLInputElement).value),
                onBlur: () => void commitTitle(),
                onFocus: selectInputText,
                onKeyDown: (e: KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const el = e.currentTarget as HTMLInputElement;
                    void commitTitle().then(() => el.blur());
                    return;
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setTitleDraft(block.title ?? '');
                    setEditingDbTitle(false);
                    (e.currentTarget as HTMLInputElement).blur();
                    return;
                  }
                  e.stopPropagation();
                },
                onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                onClick: (e: MouseEvent) => e.stopPropagation(),
              })
            : createElement(
                'button',
                {
                  type: 'button',
                  className: 'cs-db-db-title',
                  onClick: () => startEditingDbTitle(),
                  onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                },
                block.title?.trim() || 'Untitled database',
              ),
        )}
        <PrivateFlagButton blockId={block.id} />
        <FavoriteStarButton blockId={block.id} />
        {onOpenDatabase ? (
          <Pressable
            onPress={onOpenDatabase}
            className="rounded-md border border-border px-2 py-1"
          >
            <Text className="text-xs font-medium text-fg-muted">Open</Text>
          </Pressable>
        ) : null}
      </View>

      {viewConfig
        ? createElement(
            'div',
            { className: 'cs-db-view-header' },
            createElement(DatabaseViewTabs, {
              viewConfig,
              columns,
              disabled: schemaSaving,
              onChange: handleViewConfigChange,
            }),
            createElement(DatabaseToolbar, {
              columns,
              search,
              onSearchChange: setSearch,
              filters,
              onFiltersChange: (next) => patchActiveView({ filters: next }),
              sorts,
              onSortsChange: (next) => patchActiveView({ sorts: next }),
              groupBy: isTableView ? (activeView?.groupBy ?? null) : null,
              onGroupByChange: isTableView
                ? (next) => patchActiveView({ groupBy: next })
                : () => {},
              disabled: schemaSaving,
              onImportCsv: openCsvFilePicker,
              onExport: (format) => void handleExport(format),
              exportBusy,
              tableLayout: isTableView
                ? {
                    columns,
                    hiddenColumns: activeView?.hiddenColumns,
                    frozenTitle: activeView?.frozenTitle,
                    wrapText: activeView?.wrapText,
                    onHiddenColumnsChange: (hidden) => patchActiveView({ hiddenColumns: hidden }),
                    onFrozenTitleChange: (frozen) =>
                      patchActiveView({ frozenTitle: frozen ? true : undefined }),
                    onWrapTextChange: (wrap) =>
                      patchActiveView({ wrapText: wrap ? true : undefined }),
                  }
                : undefined,
              boardLayout:
                isBoardView && boardSelectColumns.length > 0
                  ? {
                      selectColumns: boardSelectColumns,
                      boardColumnId: boardSelectColumns.some((c) => c.id === boardColumnId)
                        ? boardColumnId
                        : undefined,
                      onBoardColumnChange: (id) => patchActiveView({ boardColumnId: id }),
                    }
                  : undefined,
              calendarLayout:
                isCalendarView && dateCols.length > 0
                  ? {
                      dateColumns: dateCols,
                      calendarColumnId: dateCols.some((c) => c.id === calendarColumnId)
                        ? calendarColumnId
                        : undefined,
                      onCalendarColumnChange: (id) => patchActiveView({ calendarColumnId: id }),
                      colorColumns: colorCols,
                      colorColumnId,
                      onColorColumnChange: (id) => patchActiveView({ colorColumnId: id }),
                    }
                  : undefined,
              timelineLayout:
                isTimelineView && dateCols.length > 0
                  ? {
                      dateColumns: dateCols,
                      timelineColumnId: dateCols.some((c) => c.id === timelineColumnId)
                        ? timelineColumnId
                        : undefined,
                      onTimelineColumnChange: (id) => patchActiveView({ timelineColumnId: id }),
                      colorColumns: colorCols,
                      colorColumnId,
                      onColorColumnChange: (id) => patchActiveView({ colorColumnId: id }),
                    }
                  : undefined,
              onAddColumn: isTableView
                ? (name, type) => {
                    void persistSchema([...columns, { id: newBlockId(), name, type }]);
                  }
                : undefined,
              onBeforeAddColumn: isTableView
                ? () => setColumnMenu({ kind: 'closed' })
                : undefined,
            }),
          )
        : null}

      {isBoardView && boardPickColumn
        ? createElement(
            'div',
            { className: 'cs-db-board-column-picker' },
            createElement('label', { className: 'cs-db-popover-label' }, 'Group by'),
            createElement(
              'select',
              {
                className: 'cs-db-popover-select',
                value: boardColumnId ?? '',
                disabled: schemaSaving,
                onChange: (e: Event) => {
                  const id = (e.target as HTMLSelectElement).value;
                  if (id) {
                    patchActiveView({ boardColumnId: id });
                  }
                },
                onMouseDown: (e: MouseEvent) => e.stopPropagation(),
              },
              ...boardSelectColumns.map((col) =>
                createElement('option', { key: col.id, value: col.id }, col.name),
              ),
            ),
          )
        : null}

      {createElement('input', {
        ref: csvInputRef,
        type: 'file',
        accept: '.csv,text/csv',
        style: { display: 'none' },
        onChange: (e: Event) => {
          const input = e.target as HTMLInputElement;
          const file = input.files?.[0];
          input.value = '';
          if (file) {
            void handleCsvFileSelected(file);
          }
        },
      })}

      {createElement(ImportDialog, {
        open: importDialogOpen,
        table: importTable,
        existingColumns: columns,
        onClose: () => {
          if (!schemaSaving) {
            setImportDialogOpen(false);
            setImportTable(null);
          }
        },
        onConfirm: handleImportConfirm,
      })}

      {isBoardView
        ? boardSetup
          ? createElement(DatabaseViewSetup, {
              requirement: boardSetupRequirement(),
              disabled: schemaSaving,
              onAddColumn: () => void handleAddBoardStatusColumn(),
            })
          : boardColumnId
            ? createElement(DatabaseBoard, {
                groups,
                boardColumn,
                columns,
                propertiesByRowId,
                boardColumnId,
                disabled: schemaSaving || queryLoading,
                onMoveCard: moveBoardCard,
                onOpenRow,
                onPeekRow: setPeekRowId,
              })
            : null
        : isListView
          ? createElement(DatabaseList, {
              rows,
              columns,
              propertiesByRowId,
              onOpenRow,
              onPeekRow: setPeekRowId,
            })
          : isCalendarView
            ? calendarSetup
              ? createElement(DatabaseViewSetup, {
                  requirement: calendarSetupRequirement(),
                  disabled: schemaSaving,
                  onAddColumn: () => void handleAddCalendarDateColumn(),
                })
              : calendarPickColumn
                ? createElement(
                    'div',
                    { className: 'cs-db-board-column-picker' },
                    createElement('label', { className: 'cs-db-popover-label' }, 'Date column'),
                    createElement(
                      'select',
                      {
                        className: 'cs-db-popover-select',
                        value: calendarColumnId ?? '',
                        disabled: schemaSaving,
                        onChange: (e: Event) => {
                          const id = (e.target as HTMLSelectElement).value;
                          if (id) {
                            patchActiveView({ calendarColumnId: id });
                          }
                        },
                        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                      },
                      ...dateCols.map((col) =>
                        createElement('option', { key: col.id, value: col.id }, col.name),
                      ),
                    ),
                  )
                : calendarColumnId
                  ? createElement(DatabaseCalendar, {
                      rows,
                      propertiesByRowId,
                      calendarColumnId,
                      colorColumn,
                      onOpenRow,
                      onPeekRow: setPeekRowId,
                    })
                  : null
            : isTimelineView
              ? timelineSetup
                ? createElement(DatabaseViewSetup, {
                    requirement: timelineSetupRequirement(),
                    disabled: schemaSaving,
                    onAddColumn: () => void handleAddTimelineDateColumn(),
                  })
                : timelinePickColumn
                  ? createElement(
                      'div',
                      { className: 'cs-db-board-column-picker' },
                      createElement('label', { className: 'cs-db-popover-label' }, 'Date column'),
                      createElement(
                        'select',
                        {
                          className: 'cs-db-popover-select',
                          value: timelineColumnId ?? '',
                          disabled: schemaSaving,
                          onChange: (e: Event) => {
                            const id = (e.target as HTMLSelectElement).value;
                            if (id) {
                              patchActiveView({ timelineColumnId: id });
                            }
                          },
                          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                        },
                        ...dateCols.map((col) =>
                          createElement('option', { key: col.id, value: col.id }, col.name),
                        ),
                      ),
                    )
                  : timelineColumnId
                    ? createElement(DatabaseTimeline, {
                        rows,
                        propertiesByRowId,
                        timelineColumnId,
                        colorColumn,
                        onOpenRow,
                        onPeekRow: setPeekRowId,
                      })
                    : null
            : createElement(
        'div',
        { className: 'cs-db-table-block' },
        createElement(
        'div',
        {
          className: 'cs-db-table-wrap',
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          onKeyDownCapture: stopBubbleUnlessFormField,
        },
        createElement(
          'table',
          {
            className: tableClassName,
            style: { width: tableWidthPx },
          },
          createElement(
            'colgroup',
            null,
            createElement('col', {
              style: { width: titleWidth, minWidth: titleWidth, maxWidth: titleWidth },
            }),
            ...visibleCols.map((col) => {
              const w = resolveColumnWidth(col.id, 120);
              return createElement('col', {
                key: col.id,
                style: { width: w, minWidth: w, maxWidth: w },
              });
            }),
          ),
          createElement(
            'thead',
            null,
            createElement(
              'tr',
              null,
              createElement(TitleColumnHeader, {
                disabled: schemaSaving,
                widthPx: titleWidth,
                frozen: frozenTitle,
                resizeHandlers: titleResizeHandlers,
                onResizeDragStart: () => setIsColumnResizing(true),
                onResizeDragEnd: () => setIsColumnResizing(false),
              }),
              ...visibleCols.map((col) =>
                createElement(ColumnHeader, {
                  key: col.id,
                  column: col,
                  disabled: schemaSaving,
                  widthPx: resolveColumnWidth(col.id, 120),
                  resizeHandlers: columnResizeHandlers(col.id),
                  onResizeDragStart: () => setIsColumnResizing(true),
                  onResizeDragEnd: () => setIsColumnResizing(false),
                  setHeaderRef: (el) => {
                    if (el) {
                      columnHeaderRefs.current.set(col.id, el);
                    } else {
                      columnHeaderRefs.current.delete(col.id);
                    }
                  },
                  onHeaderClick: () => handleColumnHeaderClick(col.id),
                }),
              ),
            ),
          ),
          ...(tableGroupBy ? renderGroupedBodies() : renderFlatBody()),
        ),
        createElement(ColumnMenusHost, {
              columns,
              state: columnMenu,
              setState: setColumnMenu,
              headerRefs: columnHeaderRefs,
              disabled: schemaSaving,
              onUpdateColumnConfig: (columnId, config) => {
                void patchColumnConfig(columnId, config);
              },
              onRename: (columnId, name) => {
                void persistSchema(
                  columns.map((c) => (c.id === columnId ? { ...c, name } : c)),
                );
              },
              onDelete: (columnId) => {
                void persistSchema(columns.filter((c) => c.id !== columnId));
              },
              onChangeType: (columnId, type) => {
                const col = columns.find((c) => c.id === columnId);
                if (!col || !canChangeColumnType(col)) {
                  return;
                }
                void persistSchema(
                  columns.map((c) => (c.id === columnId ? { ...c, type } : c)),
                );
              },
              onMoveLeft: (columnId) => {
                const index = columns.findIndex((c) => c.id === columnId);
                if (index <= 0) {
                  return;
                }
                const next = [...columns];
                [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
                void persistSchema(next);
              },
              onMoveRight: (columnId) => {
                const index = columns.findIndex((c) => c.id === columnId);
                if (index < 0 || index >= columns.length - 1) {
                  return;
                }
                const next = [...columns];
                [next[index], next[index + 1]] = [next[index + 1]!, next[index]!];
                void persistSchema(next);
              },
              onHide: isTableView
                ? (columnId) => {
                    patchActiveView({
                      hiddenColumns: toggleHiddenColumn(
                        activeView?.hiddenColumns,
                        columnId,
                        true,
                      ),
                    });
                  }
                : undefined,
              columnAggregates: activeView?.columnAggregates,
              onAggregateChange: handleFooterAggregateChange,
              sorts: activeView?.sorts ?? [],
              onSortsChange: (next) => patchActiveView({ sorts: next }),
        }),
        ),
        createElement(
          'div',
          {
            className: 'cs-db-table-status-slot',
            'aria-live': 'polite',
            'aria-busy': queryLoading,
          },
          queryLoading
            ? createElement(
                'div',
                { className: 'cs-db-table-status' },
                createElement(ActivityIndicator, { size: 'small' }),
                createElement(Text, { className: 'cs-db-table-status-text' }, 'Updating…'),
              )
            : null,
        ),
      )}
      {peekRowId
        ? (() => {
            const peekRow = findRowById(peekRowId);
            if (!peekRow) {
              return null;
            }
            return createElement(DatabaseRowPanel, {
              row: peekRow,
              columns,
              properties: propertiesByRowId[peekRowId] ?? [],
              disabled: schemaSaving,
              onClose: () => setPeekRowId(null),
              onOpenFullPage: onOpenRow ? () => onOpenRow(peekRowId) : undefined,
              onSaveTitle: async (title) => {
                const row = findRowById(peekRowId);
                if (!row) {
                  return;
                }
                const next = title.trim() || 'Untitled';
                if (next === (row.title ?? '')) {
                  return;
                }
                try {
                  const updated = await updateBlock(peekRowId, {
                    title: next,
                    expectedSyncVersion: row.syncVersion,
                  });
                  setRows((prev) =>
                    prev.map((r) => (r.id === peekRowId ? { ...r, title: updated.title } : r)),
                  );
                  setGroups((prev) =>
                    prev.map((g) => ({
                      ...g,
                      rows: g.rows.map((r) =>
                        r.id === peekRowId ? { ...r, title: updated.title } : r,
                      ),
                    })),
                  );
                } catch (err) {
                  try {
                    await handleConflict(err);
                  } catch (e) {
                    setError(e instanceof Error ? e.message : 'Failed to rename row');
                  }
                }
              },
              onSaveCell: (propertyId, columnType, value) =>
                saveCell(peekRowId, propertyId, columnType, value),
              onEnsureSelectOption: ensureSelectOption,
              onDateFormatChange: patchColumnDateFormat,
            });
          })()
        : null}
    </View>
  );
}
